/**
 * Async value search for rule-modal action fields — reuses QueryBuilder
 * local MultiSelectWithTag search behavior (top_values + BigQuery + footer save).
 */

import { useMemo } from "react";
import type { QueryBuilderConfig } from "../../../data-model/queryBuilder";
import {
  useQueryBuilderValueSearch,
  type QueryBuilderValueSearchProps,
} from "../../queryBuilder/useQueryBuilderValueSearch";
import { getActionValueOptionsAsMultiSelect } from "./ruleFormConstants";

/**
 * Returns props to spread onto MultiSelectWithTag for a rule action value field.
 * When search returns no matches, the typed query can be added as a custom tag.
 *
 * @param conditionConfig - Condition query builder config (top_values on fields)
 * @param selectedField - Selected action field column name
 * @param currentValue - Current action value (string or string[])
 * @returns Props for the local modal MultiSelectWithTag (same search API as query builder)
 */
export function useActionValueSearch(
  conditionConfig: QueryBuilderConfig,
  selectedField: string,
  currentValue: string | string[],
): QueryBuilderValueSearchProps {
  const initialOptions = useMemo(
    () =>
      getActionValueOptionsAsMultiSelect(
        conditionConfig,
        selectedField,
        currentValue,
      ),
    [conditionConfig, selectedField, currentValue],
  );

  return useQueryBuilderValueSearch({
    columnName: selectedField,
    initialOptions,
    resetDeps: [selectedField],
    allowCustomValueOnEmptySearch: true,
  });
}
